import { supabase } from './supabase';
import { logger } from './logger';

export interface AdminCandidate {
  /** id de `candidate_profiles`, null tant que l'onboarding n'a rien écrit */
  id: string | null;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  status: string;
  registeredAt: string;
  profileComplete: boolean;
  identityVerified: boolean;
  photoUrl: string | null;
  seedTag: string | null;
  /** false = compte créé mais aucune donnée métier saisie */
  hasProfile: boolean;
}

export interface AdminEmployer {
  /** id de `employer_profiles`, null tant que l'inscription n'a rien écrit */
  id: string | null;
  userId: string;
  companyName: string;
  siret: string;
  siretVerified: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  subscriptionActive: boolean;
  registeredAt: string;
  jobOffersCount: number;
  seedTag: string | null;
  /** false = compte créé mais aucune donnée entreprise saisie */
  hasProfile: boolean;
}

/**
 * PostgREST renvoie l'embed sous forme d'objet ou de tableau selon qu'il détecte
 * la contrainte UNIQUE sur la FK. On normalise pour ne pas en dépendre.
 */
function firstEmbed<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

/**
 * Source de vérité : `profiles`, pas `candidate_profiles`. Un compte dont
 * l'onboarding n'a jamais abouti n'a pas de ligne métier — il doit rester
 * visible côté admin, sinon l'inscription paraît n'avoir jamais eu lieu.
 */
export async function fetchCandidates(): Promise<AdminCandidate[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id,
      email,
      phone,
      status,
      seed_tag,
      created_at,
      candidate_profiles (
        id,
        first_name,
        last_name,
        city,
        profile_complete,
        identity_verified,
        photo_url
      )
    `)
    .eq('role', 'candidate')
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_candidates', 'userService', { error: error.message });
    throw error;
  }

  const rows = data || [];
  const withoutProfile = rows.filter((r: any) => !firstEmbed(r.candidate_profiles)).length;
  logger.technical('fetch_candidates_ok', 'userService', {
    count: rows.length,
    withoutProfile,
  });

  return rows.map((p: any) => {
    const c = firstEmbed<any>(p.candidate_profiles);
    return {
      id: c?.id ?? null,
      userId: p.id,
      firstName: c?.first_name || '',
      lastName: c?.last_name || '',
      email: p.email || '',
      phone: p.phone || '',
      city: c?.city || '',
      status: p.status || 'pending_verification',
      registeredAt: new Date(p.created_at).toLocaleDateString('fr-FR'),
      profileComplete: c?.profile_complete || false,
      identityVerified: c?.identity_verified || false,
      photoUrl: c?.photo_url ?? null,
      seedTag: p.seed_tag || null,
      hasProfile: !!c,
    };
  });
}

/** Même principe que fetchCandidates : on part de `profiles`. */
export async function fetchEmployers(): Promise<AdminEmployer[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id,
      email,
      phone,
      status,
      seed_tag,
      created_at,
      employer_profiles (
        id,
        first_name,
        last_name,
        company_name,
        siret,
        siret_verified,
        email,
        phone,
        company_city,
        subscription_active
      )
    `)
    .eq('role', 'employer')
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_employers', 'userService', { error: error.message });
    throw error;
  }

  const rows = data || [];
  const withoutProfile = rows.filter((r: any) => !firstEmbed(r.employer_profiles)).length;
  logger.technical('fetch_employers_ok', 'userService', {
    count: rows.length,
    withoutProfile,
  });

  const counts: Record<string, number> = {};
  const { data: offers } = await supabase
    .from('job_offers')
    .select('employer_id');
  (offers || []).forEach((o: any) => {
    counts[o.employer_id] = (counts[o.employer_id] || 0) + 1;
  });

  return rows.map((p: any) => {
    const e = firstEmbed<any>(p.employer_profiles);
    return {
      id: e?.id ?? null,
      userId: p.id,
      companyName: e?.company_name || '',
      siret: e?.siret || '',
      siretVerified: e?.siret_verified || false,
      firstName: e?.first_name || '',
      lastName: e?.last_name || '',
      email: e?.email || p.email || '',
      phone: e?.phone || p.phone || '',
      city: e?.company_city || '',
      subscriptionActive: e?.subscription_active || false,
      registeredAt: new Date(p.created_at).toLocaleDateString('fr-FR'),
      jobOffersCount: e?.id ? counts[e.id] || 0 : 0,
      seedTag: p.seed_tag || null,
      hasProfile: !!e,
    };
  });
}

export async function disableAccount(userId: string, disabled: boolean): Promise<void> {
  logger.functional(disabled ? 'disable_account' : 'enable_account', 'userService', { userId });
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-disable-account`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ userId, disabled }),
    }
  );

  const body = await res.json();
  if (!res.ok) {
    logger.error('disable_account', 'userService', { userId, error: body.error, status: res.status });
    throw new Error(body.error || 'Failed');
  }
  logger.technical('disable_account_ok', 'userService', { userId, status: body.status });
}

export async function resetPassword(userId: string, newPassword: string): Promise<void> {
  logger.functional('reset_password', 'userService', { userId });
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reset-password`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ userId, newPassword }),
    }
  );

  const body = await res.json();
  if (!res.ok) {
    logger.error('reset_password', 'userService', { userId, error: body.error, status: res.status });
    throw new Error(body.error || 'Failed');
  }
  logger.technical('reset_password_ok', 'userService', { userId });
}
